import { useRef, useState, useEffect, type ReactNode } from 'react';
import { ChevronDown, ChevronRight, MoreHorizontal } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getPaletteColor } from '@/core/lib/colors';
import { cn } from '@/core/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu';
import { Input } from '@/shared/components/ui/input';
import { Button } from '@/shared/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';

interface SectionGroupProps {
  groupId: string;
  label: string;
  color?: string;
  count: number;
  defaultOpen?: boolean;
  children: ReactNode;
  onSelectAll?: () => void;
  onRename?: (name: string) => void;
}

export function SectionGroup({
  groupId,
  label,
  color,
  count,
  defaultOpen = true,
  children,
  onSelectAll,
  onRename,
}: SectionGroupProps) {
  const { t } = useTranslation('tasks');
  const [open, setOpen] = useState(defaultOpen);
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState(label);
  const renameCardRef = useRef<HTMLDivElement>(null);
  const colorValue = color ? (getPaletteColor(color)?.value ?? undefined) : undefined;

  const hasActions = !!(onSelectAll || onRename);

  useEffect(() => {
    if (!isRenaming) return;
    function handleOutsideClick(e: MouseEvent) {
      if (renameCardRef.current && !renameCardRef.current.contains(e.target as Node)) {
        setIsRenaming(false);
      }
    }
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [isRenaming]);

  function handleRenameOpen() {
    setRenameValue(label);
    setIsRenaming(true);
  }

  function handleRenameSave() {
    const trimmed = renameValue.trim();
    if (trimmed && trimmed !== label) {
      onRename?.(trimmed);
    }
    setIsRenaming(false);
  }

  function handleRenameKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleRenameSave();
    } else if (e.key === 'Escape') {
      setIsRenaming(false);
    }
  }

  return (
    <div data-testid={`tasks-section-group-${groupId}`}>
      {/* Header — relative so the rename card anchors to its bottom-left */}
      <div className="group/section-header relative flex w-full items-center gap-2 px-4 py-2">
        <button
          type="button"
          data-testid={`tasks-section-group-${groupId}-collapse`}
          className="flex items-center gap-2 rounded px-1 py-0.5 text-left hover:bg-accent/50"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
        >
          {open ? (
            <ChevronDown className="size-3.5 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronRight className="size-3.5 shrink-0 text-muted-foreground" />
          )}
          {colorValue && (
            <span
              className="size-2.5 shrink-0 rounded-full"
              style={{ background: colorValue }}
              aria-hidden
            />
          )}
          <span className="text-sm font-medium">{label}</span>
          <span className="ml-1 text-xs text-muted-foreground">{count}</span>
        </button>

        {hasActions && (
          <DropdownMenu>
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    data-testid={`tasks-section-group-${groupId}-actions-btn`}
                    className={cn(
                      'rounded p-1 text-muted-foreground transition-opacity hover:bg-accent hover:text-foreground',
                      'opacity-100 md:opacity-0 md:group-hover/section-header:opacity-100 md:data-[state=open]:opacity-100',
                    )}
                    aria-label={t('groupHeader.renameTitle')}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <MoreHorizontal className="size-3.5" />
                  </button>
                </DropdownMenuTrigger>
              </TooltipTrigger>
              <TooltipContent>{t('groupHeader.renameTitle')}</TooltipContent>
            </Tooltip>

            <DropdownMenuContent align="start">
              {onSelectAll && (
                <DropdownMenuItem
                  data-testid={`tasks-section-group-${groupId}-select-all`}
                  onSelect={() => onSelectAll()}
                >
                  {t('groupHeader.selectAll')}
                </DropdownMenuItem>
              )}
              {onRename && (
                <DropdownMenuItem
                  data-testid={`tasks-section-group-${groupId}-rename`}
                  onSelect={handleRenameOpen}
                >
                  {t('groupHeader.rename')}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {/* Rename card — anchored to bottom-left of the header via absolute + top-full */}
        {isRenaming && (
          <div
            ref={renameCardRef}
            data-testid={`tasks-section-group-${groupId}-rename-popover`}
            className="absolute left-4 top-full z-50 w-64 rounded-md border bg-popover p-2 shadow-md"
          >
            <p className="mb-2 text-xs font-medium">{t('groupHeader.renameTitle')}</p>
            <Input
              data-testid={`tasks-section-group-${groupId}-rename-input`}
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              className="mb-2 h-7 text-xs"
              autoFocus
              onKeyDown={handleRenameKeyDown}
            />
            <div className="flex justify-end gap-1.5">
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs"
                data-testid={`tasks-section-group-${groupId}-rename-cancel`}
                onClick={() => setIsRenaming(false)}
              >
                {t('groupHeader.renameCancel')}
              </Button>
              <Button
                size="sm"
                className="h-6 px-2 text-xs"
                data-testid={`tasks-section-group-${groupId}-rename-save`}
                disabled={!renameValue.trim()}
                onClick={handleRenameSave}
              >
                {t('groupHeader.renameSave')}
              </Button>
            </div>
          </div>
        )}
      </div>

      {open && <div>{children}</div>}
    </div>
  );
}
